import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

// Push natif (iOS APNs via FCM / Android FCM).
// Le backend stocke déjà `deviceToken` + `deviceId` lors du login (collection
// `devices`). On réutilise donc l'existant : on écrit le token natif dans
// `localStorage.deviceToken` (lu par les appels login/register/verify) et un
// `deviceId` unique dans `localStorage.deviceId`.

let cachedToken: string | null = null;
let initialized = false;

function readLS(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLS(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/** Identifiant d'appareil stable. Web = valeur historique ; natif = UUID unique. */
export function getNativeDeviceId(): string {
  if (!Capacitor.isNativePlatform()) {
    return `${navigator.product}_${navigator.productSub}`;
  }
  let id = readLS('deviceId');
  if (!id) {
    id = `app-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    writeLS('deviceId', id);
  }
  return id;
}

/** Token FCM courant (cache mémoire → localStorage). */
export function getNativePushToken(): string | null {
  return cachedToken || readLS('deviceToken');
}

/** Initialise le push natif (no-op sur le web). */
export async function initNativePush(): Promise<void> {
  if (!Capacitor.isNativePlatform() || initialized) return;
  initialized = true;

  // deviceId unique (remplace navigator.product sur mobile).
  getNativeDeviceId();

  try {
    const perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== 'granted') return;

    const { token } = await FirebaseMessaging.getToken();
    if (token) {
      cachedToken = token;
      writeLS('deviceToken', token);
    }

    FirebaseMessaging.addListener('tokenReceived', (event) => {
      if (event?.token) {
        cachedToken = event.token;
        writeLS('deviceToken', event.token);
      }
    });

    FirebaseMessaging.addListener('notificationReceived', (event) => {
      // Notification reçue app au premier plan : l'app gère son propre affichage.
      console.log('[push] notificationReceived', event?.notification?.title);
    });

    FirebaseMessaging.addListener('notificationActionPerformed', (event: any) => {
      const url = event?.notification?.data?.url;
      if (url) {
        try {
          window.location.assign(url);
        } catch {
          /* ignore */
        }
      }
    });
  } catch (e) {
    console.warn('[push] init failed', e);
  }
}
